import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { config } from '@/core/config'
import { useAuthStore } from '@/store/auth.store'
import { 
  Employee, 
  ApiResponse, 
  EmployeeListResponse,
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  CityLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
  JobLookup,
  StatusLookup,
} from '@/api/types'

/**
 * Employee management service
 * Handles all employee-related API operations with proper typing
 */
export class EmployeesService {
  /**
   * Get paginated list of employees
   */
  async getAllEmployees(
    branchId: number, 
    pageNumber: number, 
    pageSize: number
  ): Promise<EmployeeListResponse> {
    const url = endpoints.employees.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<EmployeeListResponse> | EmployeeListResponse>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data && 'success' in response.data) {
      return (response.data as ApiResponse<EmployeeListResponse>).data
    }
    // API returns direct response with structure: { totalRecords, pageNumber, pageSize, data }
    return response.data as EmployeeListResponse
  }

  /**
   * Get single employee by ID
   */
  async getEmployeeById(branchId: number, id: number): Promise<Employee> {
    const url = endpoints.employees.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Employee> | Employee>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data) {
      return (response.data as ApiResponse<Employee>).data
    }
    return response.data as Employee
  }

  /**
   * Create new employee (multipart/form-data).
   * Uses fetch so request matches Swagger exactly: no axios transforms, browser sets Content-Type with boundary.
   */
  async saveEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const baseUrl = config.api.baseUrl?.replace(/\/$/, '') || ''
    const path = endpoints.employees.save
    const url = `${baseUrl}${path.startsWith('/') ? path : '/' + path}`
    const { token } = useAuthStore.getState()
    const headers: HeadersInit = {
      Accept: 'application/json',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw Object.assign(new Error(body?.message ?? body?.Message ?? `Save failed (${res.status})`), {
        response: { status: res.status, data: body },
      })
    }
    return body as ApiResponse<Employee>
  }

  /**
   * Update existing employee (multipart/form-data).
   * Do not set Content-Type: apiClient interceptor removes it for FormData so browser sets boundary.
   */
  async updateEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const response = await apiClient.put<ApiResponse<Employee>>(endpoints.employees.update, formData)
    return response.data
  }

  /**
   * Delete employee by ID
   */
  async deleteEmployee(id: number): Promise<void> {
    const url = endpoints.employees.delete(id)
    await apiClient.delete(url)
  }

  /**
   * Get gender lookups
   */
  async getGenderLookups(): Promise<GenderLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.gender)
      console.log('Gender lookup response:', response.data)
      
      // Handle different response structures
      let data = response.data
      
      // If wrapped in ApiResponse
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      // If direct array
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: GenderLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching gender lookups:', error)
      return []
    }
  }

  /**
   * Get department lookups
   */
  async getDepartmentLookups(): Promise<DepartmentLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.departments)
      console.log('Department lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: DepartmentLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching department lookups:', error)
      return []
    }
  }

  /**
   * Get country lookups
   */
  async getCountryLookups(): Promise<CountryLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.countries)
      console.log('Country lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CountryLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching country lookups:', error)
      return []
    }
  }

  /**
   * Get city lookups
   */
  async getCityLookups(): Promise<CityLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.cities)
      console.log('City lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CityLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching city lookups:', error)
      return []
    }
  }

  /**
   * Get profession in iqama lookups
   */
  async getProfessionIqamaLookups(): Promise<ProfessionIqamaLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.professionIqama)
      console.log('Profession iqama lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: ProfessionIqamaLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching profession iqama lookups:', error)
      return []
    }
  }

  /**
   * Get notice period lookups
   */
  async getNoticePeriodLookups(): Promise<NoticePeriodLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.noticePeriod)
      console.log('Notice period lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: NoticePeriodLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching notice period lookups:', error)
      return []
    }
  }

  /**
   * Get marital status lookups
   */
  async getMaritalStatusLookups(): Promise<MaritalStatusLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.maritalStatus)
      console.log('Marital status lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: MaritalStatusLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching marital status lookups:', error)
      return []
    }
  }

  /**
   * Get job lookups
   */
  async getJobLookups(): Promise<JobLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.jobs)
      console.log('Job lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: JobLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching job lookups:', error)
      return []
    }
  }

  /**
   * Get employee status lookups (e.g. Active, Inactive).
   * Falls back to static options if API is not available.
   */
  async getStatusLookups(): Promise<StatusLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.status)
      let data = response.data
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      if (Array.isArray(data)) {
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: StatusLookup) => item.id > 0)
      }
      return []
    } catch {
      // Fallback when backend has no GetStatusLookups
      return [
        { id: 1, lookupNameEn: 'Active', lookupNameAr: 'نشط' },
        { id: 0, lookupNameEn: 'Inactive', lookupNameAr: 'غير نشط' },
      ]
    }
  }
}

export const employeesService = new EmployeesService()
